import { Eye, LogOut, ShieldAlert, X } from "lucide-react";
import { useImpersonationState } from "@/components/impersonation/useImpersonationState";

/**
 * Persistent banner shown while an admin is "Viewing as client". The admin
 * can hide it with the X button if it covers important page content; a
 * small Exit pill stays in the navbar so they can always get back to their
 * own session in one click.
 */
export default function ImpersonationBanner() {
  const { impersonator, hidden, exiting, hide, exit } = useImpersonationState();
  if (!impersonator || hidden) return null;
  return (
    <div
      className="fixed top-0 inset-x-0 z-[60] bg-amber-500 text-amber-950 text-sm shadow-lg"
      data-testid="impersonation-banner"
      role="status"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-2 flex items-center gap-3">
        <ShieldAlert className="h-4 w-4 shrink-0" />
        <p className="flex-1 min-w-0 truncate">
          <strong>Viewing as client.</strong>{" "}
          <span className="opacity-80">
            Admin: <span className="font-mono">{impersonator}</span> · All
            actions in this tab affect the client's real data.
          </span>
        </p>
        <button
          type="button"
          onClick={exit}
          disabled={exiting}
          className="inline-flex items-center gap-1.5 rounded-lg bg-amber-950/10 hover:bg-amber-950/20 px-3 py-1.5 text-xs font-medium disabled:opacity-50"
          data-testid="impersonation-exit-button"
        >
          <LogOut className="h-3 w-3" /> {exiting ? "Exiting…" : "Exit"}
        </button>
        <button
          type="button"
          onClick={hide}
          title="Hide this banner — you can still exit from the header"
          aria-label="Hide banner"
          className="inline-flex h-7 w-7 items-center justify-center rounded-lg hover:bg-amber-950/10"
          data-testid="impersonation-hide-button"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

/**
 * Compact Exit pill that sits in the Navbar whenever the admin is
 * impersonating *and* has hidden the main banner. Clicking the eye icon
 * brings the full banner back; clicking Exit drops back to the admin
 * session.
 */
export function NavbarImpersonationExit() {
  const { isActive, hidden, exiting, show, exit } = useImpersonationState();
  if (!isActive || !hidden) return null;
  return (
    <div
      className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2 py-1"
      data-testid="impersonation-header-pill"
    >
      <button
        type="button"
        onClick={show}
        title="Show the impersonation banner again"
        aria-label="Show impersonation banner"
        className="inline-flex h-6 w-6 items-center justify-center rounded-full text-amber-800 hover:bg-amber-200"
        data-testid="impersonation-show-button"
      >
        <Eye className="h-3 w-3" />
      </button>
      <span className="text-[11px] font-medium text-amber-900">
        Viewing as client
      </span>
      <button
        type="button"
        onClick={exit}
        disabled={exiting}
        className="inline-flex items-center gap-1 rounded-full bg-amber-900 px-2 py-0.5 text-[11px] font-medium text-amber-50 hover:bg-amber-950 disabled:opacity-50"
        data-testid="impersonation-header-exit-button"
      >
        <LogOut className="h-2.5 w-2.5" /> {exiting ? "…" : "Exit"}
      </button>
    </div>
  );
}
